
import React, { useState, useMemo } from 'react';
import { Appointment, AppointmentStatus } from '../types';

interface AppointmentListViewProps {
  appointments: Appointment[];
  onSelect: (app: Appointment) => void;
  onOpenSettings: () => void;
}

const AppointmentListView: React.FC<AppointmentListViewProps> = ({ appointments, onSelect, onOpenSettings }) => {
  const [selectedDate, setSelectedDate] = useState('今天');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('全部');
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const [selectedSlot, setSelectedSlot] = useState('全部');

  const dates = ['今天', '明天', '01-26', '01-27', '01-28'];
  const statuses = ['全部', '待核销', '已完成', '已退款', '已取消', '已失效'];

  // 当日期切换时，重置场次选择
  React.useEffect(() => {
    setSelectedSlot('全部');
  }, [selectedDate]);

  // 计算各场次数量
  const slotsWithCounts = useMemo(() => {
    const counts: Record<string, number> = { '全部': 0 };
    appointments.forEach(app => {
      const matchDate = app.dateLabel === selectedDate;
      const matchStatus = statusFilter === '全部' || app.status === statusFilter;
      const matchQuery = !searchQuery || app.phone.includes(searchQuery) || app.name.includes(searchQuery);
      if (matchDate && matchStatus && matchQuery) {
        counts['全部']++;
        const slotName = app.timeSlot.split(' ')[0]; // 只取名称部分，如“上午场”
        if (!counts[slotName]) counts[slotName] = 0;
        counts[slotName]++;
      }
    });
    return counts;
  }, [appointments, selectedDate, statusFilter, searchQuery]);

  // 过滤预约单
  const filteredAppointments = useMemo(() => {
    return appointments.filter(app => {
      const matchDate = app.dateLabel === selectedDate;
      const matchStatus = statusFilter === '全部' || app.status === statusFilter;
      const matchQuery = !searchQuery || app.phone.includes(searchQuery) || app.name.includes(searchQuery);
      const slotName = app.timeSlot.split(' ')[0];
      const matchSlot = selectedSlot === '全部' || slotName === selectedSlot;
      return matchDate && matchStatus && matchQuery && matchSlot;
    });
  }, [appointments, selectedDate, statusFilter, searchQuery, selectedSlot]);

  const totalCount = slotsWithCounts['全部'];

  return (
    <div className="flex-1 flex flex-col bg-[#F9FAFB] overflow-hidden relative min-h-0">
      {/* Header - Optimized Padding to prevent clipping */}
      <header className="bg-white px-4 h-12 flex items-center justify-between shrink-0 border-b border-gray-100">
        <button className="w-10 h-10 flex items-center justify-start text-gray-400 active:scale-90 transition-transform shrink-0">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" /></svg>
        </button>
        <h1 className="text-[15px] font-bold text-[#333] truncate px-2">预约管理</h1>
        <button 
          onClick={onOpenSettings} 
          className="w-10 h-10 flex items-center justify-end text-gray-400 active:scale-90 transition-transform shrink-0"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        </button>
      </header>

      {/* Date Tabs - Shrink-0 is essential */}
      <div className="bg-white px-3 py-3 overflow-x-auto no-scrollbar flex space-x-2 shrink-0 border-b border-gray-50">
        {dates.map(date => (
          <button 
            key={date}
            onClick={() => setSelectedDate(date)}
            className={`px-6 py-2 rounded-lg text-[13px] font-bold transition-all shrink-0 ${selectedDate === date ? 'bg-[#5DA756] text-white' : 'bg-white border border-gray-100 text-gray-500'}`}
          >
            {date}
          </button>
        ))}
      </div>

      {/* Search & Filter Row - Shrink-0 is essential */}
      <div className="bg-white px-4 py-3 flex space-x-3 shrink-0">
        <div className="flex-1 h-10 bg-[#F5F6F8] rounded-lg px-3 flex items-center space-x-2">
          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
          <input 
            type="text" 
            placeholder="请输入手机号" 
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="bg-transparent border-none outline-none text-[13px] flex-1 text-gray-700"
          />
        </div>
        <button 
          onClick={() => setIsFilterOpen(!isFilterOpen)}
          className="h-10 bg-[#F5F6F8] rounded-lg px-4 flex items-center space-x-2 text-[13px] font-bold text-gray-700 active:bg-gray-100 transition-colors"
        >
          <span>{statusFilter}</span>
          <svg className={`w-3.5 h-3.5 transition-transform ${isFilterOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" /></svg>
        </button>
      </div>

      {/* Time Slot Tabs - Horizontal Scroll */}
      <div className="bg-white px-4 overflow-x-auto no-scrollbar flex space-x-8 shrink-0 border-b border-gray-50">
        {Object.keys(slotsWithCounts).map(slot => (
          <button 
            key={slot}
            onClick={() => setSelectedSlot(slot)}
            className="relative py-3 shrink-0 flex flex-col items-center group"
          >
            <span className={`text-[14px] font-bold transition-colors ${selectedSlot === slot ? 'text-gray-900' : 'text-gray-400 group-active:text-gray-600'}`}>
              {slot} ({slotsWithCounts[slot]})
            </span>
            {selectedSlot === slot && (
              <div className="absolute bottom-0 w-6 h-1 bg-[#5DA756] rounded-full animate-in fade-in slide-in-from-bottom-1 duration-200"></div>
            )}
          </button>
        ))}
      </div>

      {/* Scrollable Container - Critical fix: flex-1 overflow-y-auto min-h-0 */}
      <div className="flex-1 overflow-y-auto no-scrollbar p-4 space-y-4 pb-28 min-h-0 overscroll-behavior-y-contain">
        {filteredAppointments.length === 0 ? (
          <div className="py-20 flex flex-col items-center justify-center space-y-4 text-gray-300">
            <svg className="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" /></svg>
            <p className="text-[13px] font-bold">暂无该状态下的预约单</p>
          </div>
        ) : (
          filteredAppointments.map(app => (
            <div 
              key={app.id} 
              onClick={() => onSelect(app)}
              className="bg-white p-4 rounded-2xl shadow-[0_2px_12px_rgba(0,0,0,0.02)] border border-gray-50 flex flex-col space-y-3 active:scale-[0.98] transition-all"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <span className="text-[16px] font-black text-gray-900">{app.name}</span>
                  <span className="text-[11px] font-bold text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">{app.peopleCount}人</span>
                </div>
                <span className={`text-[11px] font-black px-3 py-1 rounded-full ${app.status === '待核销' ? 'bg-[#F2FBF2] text-[#5DA756]' : 'bg-gray-100 text-gray-400'}`}>
                  {app.status}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-1.5">
                  <span className="text-[13px] font-bold text-gray-400 tracking-wider">{app.phone}</span>
                  <svg className="w-3.5 h-3.5 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Filter Popover */}
      {isFilterOpen && (
        <div className="absolute inset-0 z-[1000] animate-in fade-in duration-200">
          <div className="absolute inset-0 bg-black/40" onClick={() => setIsFilterOpen(false)}></div>
          <div className="absolute right-4 top-[108px] w-36 bg-white rounded-xl shadow-2xl border border-gray-100 py-2 animate-in slide-in-from-top-2 duration-200 overflow-hidden">
            {statuses.map(status => (
              <button 
                key={status}
                onClick={() => { setStatusFilter(status); setIsFilterOpen(false); }}
                className="w-full px-4 py-3 flex items-center justify-between active:bg-gray-50 transition-colors"
              >
                <span className={`text-[13px] font-bold ${statusFilter === status ? 'text-[#333]' : 'text-gray-500'}`}>{status}</span>
                <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${statusFilter === status ? 'border-blue-500' : 'border-gray-200'}`}>
                  {statusFilter === status && <div className="w-2 h-2 bg-blue-500 rounded-full"></div>}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AppointmentListView;
